import React, { useState } from 'react';
import { Zap, TrendingUp, Database, BarChart3 } from 'lucide-react';

const PerformanceOptimizer = ({ project }) => {
  const [analysis] = useState({
    slow_queries: [
      { query: 'SELECT * FROM BSEG WHERE...', execution_ms: 4500, recommendation: 'Add index on BUKRS, BELNR, GJAHR', improvement: '85%' },
      { query: 'CDS View: Z_SALES_ANALYTICS', execution_ms: 3200, recommendation: 'Optimize join conditions, add WHERE clause', improvement: '60%' }
    ],
    cds_views: [
      { name: 'Z_MATERIAL_STOCK', status: 'optimizable', current_ms: 2100, optimized_ms: 450, improvement: '78%' },
      { name: 'Z_CUSTOMER_ORDERS', status: 'optimal', current_ms: 320, optimized_ms: 320, improvement: '0%' }
    ],
    hana_indexes: [
      { table: 'BSEG', missing_indexes: 2, recommended: 'Create on BUKRS, GJAHR, BELNR' },
      { table: 'ACDOCA', missing_indexes: 1, recommended: 'Create on RLDNR, RBUKRS, GJAHR' }
    ]
  });

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-md p-6">
        <h2 className="text-3xl font-bold mb-2">Performance & Analytics Optimizer</h2>
        <p className="text-gray-600">Detect slow queries and optimize HANA performance</p>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        <div className="bg-red-500 rounded-xl p-6 text-white">
          <Zap className="w-10 h-10 mb-3 opacity-80" />
          <div className="text-3xl font-bold mb-2">{analysis.slow_queries.length}</div>
          <div className="text-sm">Slow Queries</div>
        </div>
        <div className="bg-blue-500 rounded-xl p-6 text-white">
          <BarChart3 className="w-10 h-10 mb-3 opacity-80" />
          <div className="text-3xl font-bold mb-2">{analysis.cds_views.length}</div>
          <div className="text-sm">CDS Views</div>
        </div>
        <div className="bg-orange-500 rounded-xl p-6 text-white">
          <Database className="w-10 h-10 mb-3 opacity-80" />
          <div className="text-3xl font-bold mb-2">{analysis.hana_indexes.reduce((sum, h) => sum + h.missing_indexes, 0)}</div>
          <div className="text-sm">Missing Indexes</div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-md p-6">
        <h3 className="text-xl font-bold mb-4">Slow Queries</h3>
        <div className="space-y-3">
          {analysis.slow_queries.map((q, i) => (
            <div key={i} className="border-2 border-red-200 bg-red-50 rounded-lg p-4">
              <div className="flex items-start justify-between mb-2">
                <code className="text-sm text-gray-800 flex-1">{q.query}</code>
                <span className="text-lg font-bold text-red-600">{q.execution_ms}ms</span>
              </div>
              <p className="text-sm text-gray-600 mb-2">✓ {q.recommendation}</p>
              <p className="text-sm text-green-600 font-semibold">Expected improvement: {q.improvement}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-md p-6">
        <h3 className="text-xl font-bold mb-4">CDS View Analysis</h3>
        <div className="space-y-3">
          {analysis.cds_views.map((view, i) => (
            <div key={i} className="border-2 border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-semibold">{view.name}</h4>
                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                  view.status === 'optimal' ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'
                }`}>{view.status}</span>
              </div>
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Current</p>
                  <p className="font-semibold">{view.current_ms}ms</p>
                </div>
                <div>
                  <p className="text-gray-500">Optimized</p>
                  <p className="font-semibold text-green-600">{view.optimized_ms}ms</p>
                </div>
                <div>
                  <p className="text-gray-500">Improvement</p>
                  <p className="font-semibold text-blue-600">{view.improvement}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PerformanceOptimizer;